import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { SetRequired } from 'type-fest';

import CallToAction from '../../CallToAction';
import { Link } from '../../generic/Link';
import { IconText, icons } from '../../generic/Icon';
import List from '../../generic/List';
import PageHeadingBreadcrumbs from '../../generic/PageHeadingBreadcrumbs';
import { PackageInventoryLicenses } from '../shared';
import PackageVersionCTAMetadataLoading from './PackageVersionCTAMetadataLoading';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import usePackageVersionDetails from '../hooks/usePackageVersionDetails';
import { nuc } from '../../../utils';
import PackageDetailsMetadataDescription from '../PackageDetails/PackageDetailsMetadataDescription';
import FetcherIcon from '../../generic/FetcherIcon';
import Locator from '../../../../shared/Locator';
import type { PackageInventoryParams } from '../PackageInventoryContainer';
import TeamSelectionMenu from '../../shared/teams/TeamSelectionMenu';

type Props = {
  params: SetRequired<PackageInventoryParams, 'packageLocator' | 'version'>
};

/**
 * PackageVersionCTA is the call-to-action for the package version
 * page (ie: `/package/:packageLocator/:version`).
 *
 * It displays the page heading "breadcrumbs" for packages, and
 * then also displays the package metadata.
 */
const PackageVersionCTA: VoidFunctionComponent<Props> = ({ params }) => {
  const { status, data } = usePackageVersionDetails(params);
  const stateQueryString = useSelector(PackageInventoryStore.selectors.getUnpaginatedPackagesStateQueryString);
  const { fetcher } = Locator.parse(data?.locator || '');

  return (
    <CallToAction>
      <PageHeadingBreadcrumbs
        truncateAt={40}
        crumbs={[
          {
            to: '/packages',
            text: 'Packages',
          },
          {
            content: <TeamSelectionMenu />,
          },
          {
            to: `/packages/${nuc(params.packageLocator)}?${stateQueryString}`,
            //  Use the package name if it exists, otherwise show loading/packageLocator
            // eslint-disable-next-line no-nested-ternary -- It's fine
            text: data?.name ? data.name : (
              status.isLoading ? '...' : data?.name || params.packageLocator
            ),
          },
          {
            text: params.version,
          },
        ]}
      />
      {(status.isLoading && !data) || !data ? (
        <PackageVersionCTAMetadataLoading />
      ) : (
        <>
          <div className="package-inventory__details__metadata">
            <div className="margin-bottom-sm">
              <h2 className="package-inventory__details__metadata__subtitle package-inventory__details__metadata__subtitle--dark">
                {data.locator}
              </h2>
            </div>
            <PackageDetailsMetadataDescription
              description={data.description}
              hasDarkBackground
            />
          </div>
          <List.RowDetails isDark>
            <List.RowDetailsItem>
              <FetcherIcon fetcher={fetcher || ''} />
              {data?.packageManager && (
                <span className="margin-left-xs">
                  {data.packageManager}
                </span>
              )}
            </List.RowDetailsItem>
            <List.RowDetailsIconItem icon={icons.folders} count={data.numberOfProjects}>
              Projects
            </List.RowDetailsIconItem>
            <PackageInventoryLicenses licenses={data.licenses} hasDarkBackground />
            <List.RowDetailsItem>
              <Link className="package-inventory__metadata__link" to={data.url} openInNewTab>
                <IconText text="Homepage" icon={icons.externalLink} alignIcon="right" />
              </Link>
            </List.RowDetailsItem>
          </List.RowDetails>
        </>
      )}
    </CallToAction>
  );
};

export default PackageVersionCTA;
